import { z } from 'zod'

import { ContentStatusSchema, ShareScopeSchema } from '~/@entities/Content'
import { CurriculumTypeSchema } from '~/@entities/Curriculum'
import { CoverDataSchema } from '~/@entities/DesignTemplate'
import {
  AnswerUnitSchema,
  KeypadTypeSchema,
  ProblemAutoScoredTypeSchema,
  ProblemGroupCaseSchema,
  ProblemLevelSchema,
  ProblemSchema,
  ProblemTypeSchema,
  ProblemVideoSchema,
} from '~/@entities/Problem'
import {
  AllGradeSubjectSchema,
  RevisionSchema,
  SchoolTypeSchema,
  SemesterSchema,
} from '~/@entities/SchoolSystem'
import { StudentIdSchema, StudentSchema, StudentStatusSchema } from '~/@entities/Student'

/***************** 교재 종류 *****************/
// 서버 WorkbookType. ALL 은 검색 요청에만 쓰는 값이라 교재가 이 값을 갖는 일은 없다.
export const WORKBOOK_TYPE = [
  'ALL',
  'PUBLIC',
  'SCHOOL',
  'CUSTOM',
  'SIGNATURE',
  'CUSTOM_SIGNATURE',
  'SCHOOL_TEST',
  'ACADEMY_GROUP',
] as const

/***************** 시그니처 교재 종류 *****************/
export const SIGNATURE_TYPE = [
  'ARITHMETIC',
  'BASIC',
  'CONCEPT',
  'HIGH',
  'SCHOOL_TEST',
  'VACATION_BOOTCAMP',
  'CONCEPT_UNIT_LIGHT',
  'N_PROBLEM',
] as const

/**
 * 보안 등급(콘텐츠 확보 방식). 시중교재와 학교별 기출만 갖는다.
 * - 시중교재 1군: 콘텐츠팀이 고른 교재를 구매해 빠른정답 입력 후 CMS 업로드 / 2군: 스캔·OCR·유사문제 추출로 등록(정답이 없을 수 있다)
 * - 내신기출 1군: 실제 시험지 원본 / 2군: 시험범위로 콘텐츠팀이 만든 시험지
 */
export const WORKBOOK_SECURING_TYPE = ['PRIMARY', 'SECONDARY'] as const

/***************** 교재 이용 형태 *****************/
// 내 교재·커스텀 시그니처 교재가 어떻게 쓰이는가. 체험(SAMPLE)·대여(RENT)·구매(OWN)·표지 편집 전(DRAFT)·
// 표지 편집용 체험(COVER_SAMPLE)·취소(CANCELED)
export const WORKBOOK_FEATURE_TYPE = [
  'DRAFT',
  'OWN',
  'CANCELED',
  'SAMPLE',
  'COVER_SAMPLE',
  'RENT',
] as const

/***************** 시그니처 교재 주문 상태 *****************/
export const SIGNATURE_WORKBOOK_ORDER_STATUS = [
  'PAYMENT_COMPLETED',
  'IN_PRODUCTION',
  'DELIVERY_COMPLETED',
  'ORDER_CANCEL',
] as const

/***************** 교재 PDF 생성 상태 *****************/
export const WORKBOOK_PDF_STATUS = ['GENERATING', 'FAIL', 'SUCCESS'] as const

/***************** 교재 문제 필터 *****************/
// 교재 목록에서 그 교재로 어떤 방식의 학습지를 만들 수 있는지로 거른다
export const WORKBOOK_FILTER_TYPE = [
  'CONCEPT',
  'CORNER',
  'CONCEPT_BY_WORKBOOK',
  'CORNER_BY_WORKBOOK',
  'SCHOOL_TEST',
] as const

/***************** 유형칩 난이도 *****************/
export const CONCEPT_CHIP_LEVEL = ['A', 'B', 'C'] as const

export const WorkbookTypeSchema = z.enum(WORKBOOK_TYPE)
export type WorkbookType = z.infer<typeof WorkbookTypeSchema>

export const SignatureTypeSchema = z.enum(SIGNATURE_TYPE)
export type SignatureType = z.infer<typeof SignatureTypeSchema>

export const WorkbookSecuringTypeSchema = z.enum(WORKBOOK_SECURING_TYPE)
export type WorkbookSecuringType = z.infer<typeof WorkbookSecuringTypeSchema>

export const WorkbookFeatureTypeSchema = z.enum(WORKBOOK_FEATURE_TYPE)
export type WorkbookFeatureType = z.infer<typeof WorkbookFeatureTypeSchema>

export const SignatureWorkbookOrderStatusSchema = z.enum(SIGNATURE_WORKBOOK_ORDER_STATUS)
export type SignatureWorkbookOrderStatus = z.infer<typeof SignatureWorkbookOrderStatusSchema>

export const WorkbookPdfStatusSchema = z.enum(WORKBOOK_PDF_STATUS)
export type WorkbookPdfStatus = z.infer<typeof WorkbookPdfStatusSchema>

export const WorkbookFilterTypeSchema = z.enum(WORKBOOK_FILTER_TYPE)
export type WorkbookFilterType = z.infer<typeof WorkbookFilterTypeSchema>

export const ConceptChipLevelSchema = z.enum(CONCEPT_CHIP_LEVEL)
export type ConceptChipLevel = z.infer<typeof ConceptChipLevelSchema>

const _localDatetime = z.iso.datetime({ local: true })

/***************** 교재 *****************/

export const WorkbookIdSchema = z.number()

// 표지·단원 구분 표지가 PDF 의 몇 쪽에 있는가
export const WorkbookCoverPagesSchema = z.object({
  mainCoverPages: z.array(z.number()),
  chapterCoverPages: z.array(z.number()),
})
export type WorkbookCoverPages = z.infer<typeof WorkbookCoverPagesSchema>

/**
 * 교재 종류 공통부. 서버 WorkbookDto 와 그 하위 클래스가 고정값을 넣는 필드가 많다
 * (시중교재·교과서는 page·minPage·maxPage·answerPage 가 0, 내 교재가 아니면 coverPages·coverType 이 null).
 * 제외한 서버 필드:
 * - includeConceptName·includeAnswerRate·includeLevel·includeTagTop: 학교별 기출 PDF 옵션의 목록 사본. 앱은 내 교재 상세의 includeXInPdf 를 읽는다
 * - defaultPdfUrl·defaultAnswerPdfUrl·fileTitle: 서버가 PDF 를 만들 때 쓰는 값
 * - edition: 서버가 항상 빈 문자열로 보낸다
 */
const _WorkbookBaseSchema = z.object({
  id: WorkbookIdSchema,
  // 내 교재는 만들 때 받은 난이도(요청이 1~5 로 검증한다), 나머지 종류는 서버가 3 으로 고정한다
  level: ProblemLevelSchema,
  // 시중교재·교과서·학원그룹 교재는 0, 나머지는 마지막 쪽 번호
  page: z.number(),
  revision: RevisionSchema,
  schoolType: SchoolTypeSchema,
  grade: AllGradeSubjectSchema,
  // 고등은 학기 대신 과목이라 null 이다
  semester: SemesterSchema.nullable(),
  headtitle: z.string(),
  title: z.string(),
  subtitle: z.string(),
  fulltitle: z.string(),
  publisher: z.string(),
  // 출제할 수 있는 교재인가(출제했는가가 아니다)
  assignmentFlag: z.boolean(),
  autoScored: z.boolean(),
  pdfStatus: WorkbookPdfStatusSchema,
  pdfUrl: z.string().nullable(),
  answerPdfUrl: z.string().nullable(),
  // 인쇄할 수 있는(저작권 문제가 없는) 교재만 쪽 범위를 갖는다. 원본 시그니처 교재에서는 미리보기 범위다
  minPage: z.number(),
  maxPage: z.number(),
  answerPage: z.number(),
  coverPages: WorkbookCoverPagesSchema.nullable(),
  coverType: z.number().nullable(),
  // 인쇄 분할 수. 인쇄(PrintDocument)의 PARTITION_TYPE 과 같은 뜻이지만 서버가 교재에는 범위를 검증하지 않는다
  partitionType: z.number(),
  // 개념 교재인가
  conceptFlag: z.boolean(),
  createDatetime: _localDatetime,
  // 쌍둥이 문제 배수
  pairX: z.number().nullable(),
  securingType: WorkbookSecuringTypeSchema.nullable(),
})

// 내 교재의 인쇄 판매가. 상품을 붙인 응답(주문 화면)에서만 채워지고 나머지는 null 이다
const _WorkbookProductSchema = z.object({
  productId: z.number().nullable(),
  productDiscountRate: z.number().nullable(),
  problemColorPrice: z.number().nullable(),
  problemBwPrice: z.number().nullable(),
  problemAnswerColorPrice: z.number().nullable(),
  problemAnswerBwPrice: z.number().nullable(),
  problemColorDiscountedPrice: z.number().nullable(),
  problemBwDiscountedPrice: z.number().nullable(),
  problemAnswerColorDiscountedPrice: z.number().nullable(),
  problemAnswerBwDiscountedPrice: z.number().nullable(),
})

/** 시중교재 */
export const PublicWorkbookSchema = _WorkbookBaseSchema.extend({
  type: z.literal('PUBLIC'),
  securingType: WorkbookSecuringTypeSchema,
  // 빠른정답을 지원하는가
  answerConfirmed: z.boolean(),
  pairFlag: z.boolean(),
  thumbnailImageUrl: z.string(),
})
export type PublicWorkbook = z.infer<typeof PublicWorkbookSchema>

/** 교과서 */
export const SchoolWorkbookSchema = _WorkbookBaseSchema.extend({
  type: z.literal('SCHOOL'),
  pairFlag: z.boolean(),
  thumbnailImageUrl: z.string(),
})
export type SchoolWorkbook = z.infer<typeof SchoolWorkbookSchema>

/**
 * 내 교재(선생님이 만든 교재). 만든 방식(customWorkbookType)·문제 조건은 목록에 오지 않고 내 교재 상세가 싣는다.
 * 제외한 서버 필드: innerType(내지 디자인 이름. 앱은 상세의 innerDesignTemplateId 를 읽는다)
 */
export const CustomWorkbookSchema = _WorkbookBaseSchema.extend({
  type: z.literal('CUSTOM'),
  coverData: CoverDataSchema.nullable(),
  designTemplateId: z.number().nullable(),
  thumbnailImageUrl: z.string().nullable(),
  thumbnailImageKey: z.string().nullable(),
  // 문제를 묶은 단원 단위(중단원·소단원)
  makingUnit: CurriculumTypeSchema,
  academyId: z.string(),
  academyGroupId: z.string().nullable(),
  teacherId: z.string(),
  featureType: WorkbookFeatureTypeSchema,
  orderDatetime: _localDatetime.nullable(),
  shareScope: ShareScopeSchema,
  ..._WorkbookProductSchema.shape,
})
export type CustomWorkbook = z.infer<typeof CustomWorkbookSchema>

/**
 * 원본 시그니처 교재. 선생님은 이것을 골라 표지를 꾸민 커스텀 시그니처 교재를 만든다.
 * 제외한 서버 필드: signatureSubtype·sampleWorkbookFlag·previewMinPageNumber·previewMaxPageNumber·solutionPdfUrl
 * (앱이 읽지 않는다)
 */
export const SignatureWorkbookSchema = _WorkbookBaseSchema.extend({
  type: z.literal('SIGNATURE'),
  signatureType: SignatureTypeSchema,
  pdfUrl: z.string(),
  pairFlag: z.boolean(),
  thumbnailImageUrl: z.string(),
})
export type SignatureWorkbook = z.infer<typeof SignatureWorkbookSchema>

/**
 * 커스텀 시그니처 교재(원본 시그니처 교재에 학원 표지를 입힌 것).
 * 체험 교재(SAMPLE)는 가상의 원본을 가리켜 signatureWorkbookId 가 -1 이다.
 * 결제 완료·주문 취소 상태와 표지 편집용 체험 교재는 썸네일이 null 이다.
 * 제외한 서버 필드: academyName(서버가 항상 null 로 보낸다)·solutionPdfUrl(앱이 읽지 않는다)
 */
export const CustomSignatureWorkbookSchema = _WorkbookBaseSchema.extend({
  type: z.literal('CUSTOM_SIGNATURE'),
  signatureType: SignatureTypeSchema,
  featureType: WorkbookFeatureTypeSchema,
  pdfUrl: z.string(),
  designTemplateId: z.number().nullable(),
  coverData: CoverDataSchema.nullable(),
  orderDatetime: _localDatetime.nullable(),
  orderStatus: SignatureWorkbookOrderStatusSchema,
  thumbnailImageUrl: z.string().nullable(),
  thumbnailImageKey: z.string().nullable(),
  academyId: z.string(),
  signatureWorkbookFulltitle: z.string(),
  signatureWorkbookId: z.number(),
  pairFlag: z.boolean(),
  productId: z.number().nullable(),
  productStatus: ContentStatusSchema.nullable(),
})
export type CustomSignatureWorkbook = z.infer<typeof CustomSignatureWorkbookSchema>

/**
 * 학원그룹 교재. 학원그룹이 직접 등록한 교재로 시그니처 교재의 변형이 아니다(북마크 비활성·영상 추적 등 동작만 같다).
 * subtype 은 학원그룹이 붙이는 자유 문자열이다.
 * 제외한 서버 필드: quickAnswerPdfUrl(앱이 읽지 않는다)
 */
export const AcademyGroupWorkbookSchema = _WorkbookBaseSchema.extend({
  type: z.literal('ACADEMY_GROUP'),
  academyGroupId: z.string(),
  subtype: z.string(),
  pairFlag: z.boolean(),
  thumbnailImageUrl: z.string(),
})
export type AcademyGroupWorkbook = z.infer<typeof AcademyGroupWorkbookSchema>

/**
 * 학교별 기출 교재. 교재 목록(GET /workbook)에는 오지 않고 학생 교재 조회에만 실린다.
 * 제외한 서버 필드: uploaderType·year·schoolTestType·school·region·provideType·시험 범위·시행 학년/학기
 * (교재로서 읽는 화면이 없다. 학교별 기출 화면은 SchoolTest 엔티티의 응답을 쓴다)
 */
export const SchoolTestWorkbookSchema = _WorkbookBaseSchema.extend({
  type: z.literal('SCHOOL_TEST'),
  securingType: WorkbookSecuringTypeSchema,
})
export type SchoolTestWorkbook = z.infer<typeof SchoolTestWorkbookSchema>

export const WorkbookSchema = z.discriminatedUnion('type', [
  PublicWorkbookSchema,
  SchoolWorkbookSchema,
  CustomWorkbookSchema,
  SignatureWorkbookSchema,
  CustomSignatureWorkbookSchema,
  AcademyGroupWorkbookSchema,
  SchoolTestWorkbookSchema,
])
export type Workbook = z.infer<typeof WorkbookSchema>
export type WorkbookId = Workbook['id']
export type WorkbookOf<T extends Workbook['type']> = Extract<Workbook, { type: T }>

/** 교재 목록(GET /workbook)에 오는 교재. 서버가 학교별 기출 검색을 막아 두어 그 종류는 오지 않는다 */
export const ListedWorkbookSchema = z.discriminatedUnion('type', [
  PublicWorkbookSchema,
  SchoolWorkbookSchema,
  CustomWorkbookSchema,
  SignatureWorkbookSchema,
  CustomSignatureWorkbookSchema,
  AcademyGroupWorkbookSchema,
])
export type ListedWorkbook = z.infer<typeof ListedWorkbookSchema>

/** 원본 교재 페이지를 보며 문제를 고를 수 있는 교재(시중교재·교과서·커스텀 시그니처·학원그룹) */
export type SupportedWorkbook = WorkbookOf<
  'PUBLIC' | 'SCHOOL' | 'CUSTOM_SIGNATURE' | 'ACADEMY_GROUP'
>
export type SupportedWorkbookType = SupportedWorkbook['type']

export const isPublicWorkbook = (workbook: Pick<Workbook, 'type'>): workbook is PublicWorkbook =>
  workbook.type === 'PUBLIC'
export const isCustomWorkbook = (workbook: Pick<Workbook, 'type'>): workbook is CustomWorkbook =>
  workbook.type === 'CUSTOM'
export const isCustomSignatureWorkbook = (
  workbook: Pick<Workbook, 'type'>,
): workbook is CustomSignatureWorkbook => workbook.type === 'CUSTOM_SIGNATURE'

/** 시그니처·커스텀 시그니처·학원그룹 교재. 북마크를 막고 풀이 영상 시청을 추적하는 동작이 같다 */
export const isSignatureOrAcademyGroupWorkbook = (type: Workbook['type']) =>
  type === 'SIGNATURE' || type === 'CUSTOM_SIGNATURE' || type === 'ACADEMY_GROUP'

/***************** 교재 상세(선생님 앱) *****************/

/**
 * `/workbook/{id}` 의 교재 한 권. 서버는 종류와 상관없이 같은 클래스(WorkbookWithFilterDto)로 공통부를 펼치고
 * 종류별 값 몇 개만 덧붙여, 종류별 필드(썸네일·빠른정답 지원 등)는 오지 않는다. 덧붙는 값은 해당 종류가 아니면 null 이다.
 * - pairFlag: 시중교재·교과서·시그니처·커스텀 시그니처
 * - signatureWorkbookId·signatureType: 시그니처(자기 id)·커스텀 시그니처(원본 id)
 * - coverData: 커스텀 시그니처·내 교재 / innerDesignTemplateId: 내 교재
 * 제외한 서버 필드: filter·problemIdList(교재를 만든 조건과 문제. 이 응답으로는 앱이 읽지 않는다)와 공통부에서 뺀 필드
 * 2026-10-14 백엔드 develop(6a0f8e7bb) 의 workbook/model/dto/WorkbookWithFilterDto.kt·WorkbookDto.kt 로 확인.
 */
export const WorkbookDetailSchema = _WorkbookBaseSchema.extend({
  type: WorkbookTypeSchema.exclude(['ALL']),
  includeConceptName: z.boolean(),
  includeAnswerRate: z.boolean(),
  includeLevel: z.boolean(),
  includeTagTop: z.boolean(),
  pairFlag: z.boolean().nullable(),
  signatureWorkbookId: z.number().nullable(),
  signatureType: SignatureTypeSchema.nullable(),
  coverData: CoverDataSchema.nullable(),
  innerDesignTemplateId: z.number().nullable(),
})
export type WorkbookDetail = z.infer<typeof WorkbookDetailSchema>

/** 교재를 받은 학생 한 명과 그 학생 교재의 최근 회차. 시그니처·학원그룹 교재 보기에서 필기를 고를 때 쓴다 */
export const WorkbookAssignedStudentSchema = z.object({
  studentWorkbookRevisionId: z.number(),
  studentId: StudentSchema.shape.id,
  studentName: StudentSchema.shape.name,
  schoolType: StudentSchema.shape.schoolType,
  grade: StudentSchema.shape.grade,
})
export type WorkbookAssignedStudent = z.infer<typeof WorkbookAssignedStudentSchema>

/** 교재 PDF 내려받기·미리보기 주소 */
export const WorkbookFileSchema = z.object({
  presignedUrl: z.string(),
  fileName: z.string(),
})
export type WorkbookFile = z.infer<typeof WorkbookFileSchema>

/***************** 교재 목록 *****************/

/** 교재를 배정받은 학생 */
export const WorkbookAssigneeSchema = z.object({
  // 학생 교재(StudentWorkbook) id
  id: z.number(),
  workbookId: z.number(),
  studentId: StudentIdSchema,
  studentName: z.string(),
  studentStatus: StudentStatusSchema,
  createDatetime: _localDatetime,
})
export type WorkbookAssignee = z.infer<typeof WorkbookAssigneeSchema>

/**
 * 교재 목록의 한 줄. 교재에 배정 정보가 붙는다.
 * - list: 배정 대상 없이 배정 포함 검색을 했을 때 교재를 가진 학생들. 그 밖에는 빈 배열
 * - assigned·lastAssignDateTime: 배정 대상(학년·반·학생)을 주고 검색했을 때만 값이 있다
 * - lastUsePageNumber: 대상 학생이 하나로 정해질 때만 값이 있다. 풀이를 시작하지 않았으면 0
 */
export const WorkbookListItemSchema = z.object({
  id: z.number(),
  workbook: ListedWorkbookSchema,
  list: z.array(WorkbookAssigneeSchema),
  assigned: z.boolean().nullable(),
  lastAssignDateTime: _localDatetime.nullable(),
  lastUsePageNumber: z.number().nullable(),
})
export type WorkbookListItem<W extends ListedWorkbook = ListedWorkbook> = Omit<
  z.infer<typeof WorkbookListItemSchema>,
  'workbook'
> & { workbook: W }

/***************** 교재 페이지 *****************/

/** 교재 한 쪽의 원본 이미지(시그니처·커스텀 시그니처·학원그룹 교재) */
export const WorkbookPageImageSchema = z.object({
  page: z.number(),
  imageUrl: z.string(),
})
export type WorkbookPageImage = z.infer<typeof WorkbookPageImageSchema>

/** 교재의 한 쪽. 시그니처·학원그룹 교재만 원본 이미지를 싣는다 */
export const WorkbookPageSchema = z.object({
  id: z.number(),
  workbookId: z.number(),
  page: z.number(),
  title: z.string(),
  imageUrl: z.string().optional(),
})
export type WorkbookPage = z.infer<typeof WorkbookPageSchema>

/***************** 교재 문제 *****************/

// 문제를 추천 묶음으로 찾는 키. 묶는 단위(groupCase)가 넓을수록 키가 짧다
const _RecommendKeySchema = z.object({
  groupCase: ProblemGroupCaseSchema,
  conceptId: z.number(),
  level: z.number(),
  topicId: z.number().optional(),
  subTopicId: z.number().optional(),
})

// 정답 AST. 구조 검증은 @mathflat/answer-expression 의 파서가 맡고, 경계에서는 객체인지만 본다.
const _AnswerDataSchema = ProblemSchema.shape.answerData

/** 시중교재·교과서 문제와 짝지은 문제은행 문제(엑스토리) */
export const WorkbookProblemXistorySchema = z.object({
  description: z.string().nullable(),
  problem: ProblemSchema.nullable(),
})
export type WorkbookProblemXistory = z.infer<typeof WorkbookProblemXistorySchema>

/**
 * 교재 문제. 문제은행의 Problem·학습지 문제와 다르다. 교재 종류마다 서버 클래스가 다르지만 응답에 종류를 가리는
 * 필드가 없어, 공통부에 종류별 필드를 선택으로 얹는다.
 * - problemId: 짝지은 문제은행 문제. 내 교재·학원그룹은 항상, 시중교재·교과서·시그니처는 있을 때만
 * - answerConfirmed: 시중교재만 값이 있다
 * - xistory: 시중교재·교과서
 * - problem: 내 교재·학원그룹 교재가 문제은행 문제를 통째로 싣는다(학생 채점 조회)
 * - video·problemImageUrl·solutionImageUrl·status: 시그니처 교재(학원그룹은 이미지만)
 * 제외한 서버 필드: tagTop(학생 채점 조회에만 오고 앱이 읽지 않는다)·schoolTestWorkbookId·score(학교별 기출)·
 * videoId·modifiedFromOriginalFlag(학원그룹)·problemImageBaseUrl·solutionImageBaseUrl·videoMatched
 */
export const WorkbookProblemSchema = z.object({
  id: z.number(),
  workbookPageId: z.number(),
  title: z.string(),
  number: z.string(),
  order: z.number(),
  conceptId: z.number(),
  topicId: z.number().nullable(),
  subTopicId: z.number().nullable(),
  groupCase: ProblemGroupCaseSchema.nullable(),
  groupCode: z.number().nullable(),
  type: ProblemTypeSchema,
  optionCount: z.number(),
  level: ProblemLevelSchema,
  levelOfConceptChip: ConceptChipLevelSchema,
  answer: z.string(),
  answerMathflat: z.string(),
  answerImageUrl: z.string().nullable(),
  autoScoredType: ProblemAutoScoredTypeSchema,
  recommendKey: _RecommendKeySchema.nullable(),
  answerData: _AnswerDataSchema,
  answerUnits: z.array(AnswerUnitSchema),
  answerCount: z.number(),
  keypadTypes: z.array(KeypadTypeSchema),
  problemId: z.number().nullable().optional(),
  answerConfirmed: z.boolean().nullable().optional(),
  xistory: WorkbookProblemXistorySchema.nullable().optional(),
  problem: ProblemSchema.nullable().optional(),
  video: ProblemVideoSchema.nullable().optional(),
  problemImageUrl: z.string().optional(),
  solutionImageUrl: z.string().optional(),
  status: ContentStatusSchema.optional(),
})
export type WorkbookProblem = z.infer<typeof WorkbookProblemSchema>

/** 원본 교재 페이지에 실린 문제와 그 페이지를 보여줄 예시·원본 문제 이미지 */
export const WorkbookPageProblemSchema = z.object({
  workbookProblem: WorkbookProblemSchema,
  exampleProblem: z.object({ id: z.number(), url: z.string() }).nullable(),
  originalProblem: z.object({ id: z.number(), url: z.string() }).nullable(),
})
export type WorkbookPageProblem = z.infer<typeof WorkbookPageProblemSchema>

/** 원본 교재의 한 쪽과 그 쪽의 문제들 */
export const WorkbookPageWithProblemsSchema = WorkbookPageSchema.extend({
  pageProblems: z.array(WorkbookPageProblemSchema),
})
export type WorkbookPageWithProblems = z.infer<typeof WorkbookPageWithProblemsSchema>

/***************** 커스텀 시그니처 교재 만들기 *****************/

/** 표지 편집 1단계(교재 정보·로고)를 서버에 맡겨 둔 임시 필터 */
export const SignatureWorkbookDraftSchema = z.object({
  filterId: z.string(),
  signatureWorkbookId: z.number(),
  designTemplateId: z.number(),
  headtitle: z.string().nullable(),
  title: z.string(),
  subtitle: z.string().nullable(),
  academyName: z.string().nullable(),
  frontLogoImageUrl: z.string().nullable(),
  backLogoImageUrl: z.string().nullable(),
})
export type SignatureWorkbookDraft = z.infer<typeof SignatureWorkbookDraftSchema>
